<script setup lang="ts">
defineOptions({ inheritAttrs: false });

import { useFormModel } from "@/hooks";
import type { InputAlign } from "@nutui/nutui";
import type { PickerOption } from "@nutui/nutui/dist/types/__VUE/picker/types";
import { computed, ref } from "vue";

const props = withDefaults(
  defineProps<{
    modelValue?: string;
    placeholder?: string;
    inputAlign?: InputAlign;
    items?: Record<string, any>[];
    valueFieldKey?: string;
    textFieldKey?: string;
  }>(),
  {
    items: () => [],
  }
);

const emit = defineEmits(["update:modelValue", "change"]);

const show = ref(false);

const options = computed(() => {
  return props.items.map<PickerOption>((item) => ({
    value: item[props.valueFieldKey || "value"],
    text: item[props.textFieldKey || "text"],
  }));
});

const text = computed(() => {
  if (!props.modelValue) return "";
  const option = options.value.find((opt) => opt.value === props.modelValue);
  if (!option) return props.modelValue;
  return option.text;
});

const model = computed(() => (props.modelValue ? [props.modelValue] : []));

function onClick() {
  show.value = true;
}

function confirm({ selectedValue }: any) {
  show.value = false;
  const value = selectedValue.length > 0 ? selectedValue[0] : "";
  emit("update:modelValue", value);
  emit("change", value);
}

const { setFormModel } = useFormModel();
defineExpose({ setFormModel });
</script>
<template>
  <div class="mp-select">
    <nut-input
      :model-value="text"
      :placeholder="placeholder"
      :input-align="inputAlign"
      :border="false"
      readonly
      type="text"
      @click="onClick"
    />
    <nut-popup v-model:visible="show" position="bottom">
      <nut-picker
        :model-value="model"
        v-bind="$attrs"
        :columns="[options]"
        @confirm="confirm"
        @cancel="show = false"
      >
        <template #default v-if="$slots.default">
          <slot name="default" />
        </template>
        <template #top v-if="$slots.top"> <slot name="top" /> </template>
      </nut-picker>
    </nut-popup>
  </div>
</template>
<style lang="scss">
.mp-select {
  width: 100%;
}
</style>
